import React, { useState } from 'react';
import './Glossary.css';

const GLOSSARY_TERMS = {
  'zero-knowledge': {
    term: 'Zero-Knowledge Proof',
    category: 'Core Concept',
    definition: 'A cryptographic method that allows one party (the prover) to prove to another party (the verifier) that a statement is true without revealing any information beyond the validity of the statement itself.',
    example: 'Proving you know a password without revealing the password. Proving your age is over 18 without revealing your birthdate.',
    relatedTerms: ['prover', 'verifier', 'witness']
  },
  'prover': {
    term: 'Prover',
    category: 'Core Concept',
    definition: 'The party in a zero-knowledge system that generates a proof to demonstrate knowledge of some information or the validity of a computation.',
    example: 'In a private transaction, you (the prover) generate a proof showing you have sufficient funds without revealing your exact balance.',
    relatedTerms: ['zero-knowledge', 'verifier', 'proof']
  },
  'verifier': {
    term: 'Verifier',
    category: 'Core Concept',
    definition: 'The party in a zero-knowledge system that checks a proof to confirm a statement is true without learning any private information.',
    example: 'A blockchain node verifying your transaction proof without seeing your account balance.',
    relatedTerms: ['zero-knowledge', 'prover', 'proof']
  },
  'witness': {
    term: 'Witness (Private Input)',
    category: 'Core Concept',
    definition: 'The secret information known only to the prover that is used to generate a proof. The witness remains hidden from the verifier.',
    example: 'Your private key, account balance, or the solution to a puzzle - information you know but don\'t want to reveal.',
    relatedTerms: ['zero-knowledge', 'prover', 'public-input']
  },
  'public-input': {
    term: 'Public Input',
    category: 'Core Concept',
    definition: 'Information that is visible to both the prover and verifier. The proof demonstrates a relationship between public inputs and private witness.',
    example: 'The minimum age requirement (18) is public, while your actual age is private. The proof shows the relationship (age > 18) holds.',
    relatedTerms: ['witness', 'zero-knowledge']
  },
  'stark': {
    term: 'STARK (Scalable Transparent ARgument of Knowledge)',
    category: 'Proof System',
    definition: 'A type of zero-knowledge proof that is quantum-resistant, doesn\'t require a trusted setup, and scales well to large computations. Miden uses STARKs.',
    example: 'STARKs enable proving the execution of millions of operations efficiently, making them ideal for blockchain scalability.',
    relatedTerms: ['zero-knowledge', 'miden', 'zk-rollup']
  },
  'circuit': {
    term: 'Arithmetic Circuit',
    category: 'Technical',
    definition: 'A mathematical representation of a computation as a series of arithmetic operations and constraints. ZK proofs verify that a circuit was executed correctly.',
    example: 'An addition circuit: inputs A and B, output C, constraint: A + B = C. ZK proves you satisfied this without revealing A or B.',
    relatedTerms: ['constraint', 'zero-knowledge', 'witness']
  },
  'constraint': {
    term: 'Constraint',
    category: 'Technical',
    definition: 'A mathematical equation or rule that must be satisfied in a zero-knowledge proof. Proofs demonstrate all constraints are met without revealing private values.',
    example: 'Constraint: "balance_before - amount = balance_after". The proof shows this holds without revealing any balance.',
    relatedTerms: ['circuit', 'zero-knowledge']
  },
  'miden': {
    term: 'Miden VM',
    category: 'Technology',
    definition: 'A stack-based zero-knowledge virtual machine that enables privacy-preserving smart contracts and computation with STARK proofs.',
    example: 'Write a private smart contract in Miden Assembly, execute it with secret inputs, and generate a proof that anyone can verify.',
    relatedTerms: ['stark', 'zero-knowledge', 'stack']
  },
  'stack': {
    term: 'Stack',
    category: 'Technical',
    definition: 'A Last-In-First-Out (LIFO) data structure where operations push values on top and pop values from the top. Miden VM is stack-based.',
    example: 'push 5, push 3, add → stack contains [8]. Operations process values in reverse order of insertion.',
    relatedTerms: ['miden', 'lifo']
  },
  'lifo': {
    term: 'LIFO (Last-In-First-Out)',
    category: 'Technical',
    definition: 'A principle where the most recently added item is the first one to be removed. Essential to understanding stack-based computation.',
    example: 'Like a stack of plates - you add to the top and take from the top.',
    relatedTerms: ['stack', 'miden']
  },
  'privacy': {
    term: 'Computational Privacy',
    category: 'Core Concept',
    definition: 'The ability to perform computations on private data while proving correctness, without revealing the underlying data.',
    example: 'Computing your tax owed and proving it\'s correct without revealing your income, deductions, or other financial details.',
    relatedTerms: ['zero-knowledge', 'witness']
  },
  'zkapp': {
    term: 'zkApp (Zero-Knowledge Application)',
    category: 'Technology',
    definition: 'An application that uses zero-knowledge proofs to provide privacy and scalability. Enables private smart contracts and confidential transactions.',
    example: 'A private voting system where votes are counted correctly but individual votes remain secret. A confidential DeFi protocol.',
    relatedTerms: ['zero-knowledge', 'miden', 'privacy']
  },
  'zk-rollup': {
    term: 'ZK-Rollup',
    category: 'Technology',
    definition: 'A blockchain scaling solution that executes transactions off-chain and uses zero-knowledge proofs to verify batches of transactions on-chain.',
    example: 'Process 1000 transactions off-chain, generate one proof, post it on-chain - saving space and gas while maintaining security.',
    relatedTerms: ['stark', 'zero-knowledge', 'scalability']
  },
  'deterministic': {
    term: 'Deterministic Execution',
    category: 'Technical',
    definition: 'A property where the same inputs always produce the same outputs. Essential for zero-knowledge proofs to be verifiable.',
    example: '5 + 3 always equals 8. The verifier can independently check the computation produces the claimed result.',
    relatedTerms: ['zero-knowledge', 'verifier']
  },
  'soundness': {
    term: 'Soundness',
    category: 'Security',
    definition: 'A security property ensuring that it\'s computationally impossible to create a valid proof for a false statement.',
    example: 'You cannot create a proof that you know the password if you actually don\'t know it - the system is sound.',
    relatedTerms: ['zero-knowledge', 'security']
  },
  'completeness': {
    term: 'Completeness',
    category: 'Security',
    definition: 'A property ensuring that if a statement is true and the prover knows the witness, they can always generate a valid proof.',
    example: 'If you really do know the password, you can always prove it - the system is complete.',
    relatedTerms: ['zero-knowledge', 'prover']
  },
  'trusted-setup': {
    term: 'Trusted Setup',
    category: 'Security',
    definition: 'A ceremony required by some ZK systems (like SNARKs) where parameters are generated. If compromised, security breaks. STARKs don\'t need this!',
    example: 'SNARKs require trusted setup, STARKs are "transparent" and don\'t. Miden uses STARKs, avoiding this security risk.',
    relatedTerms: ['stark', 'security']
  },
  'range-proof': {
    term: 'Range Proof',
    category: 'Application',
    definition: 'A ZK proof that a value lies within a specific range without revealing the exact value.',
    example: 'Proving your age is between 18-65 for insurance without revealing you\'re exactly 34 years old.',
    relatedTerms: ['zero-knowledge', 'privacy']
  },
  'membership-proof': {
    term: 'Membership Proof',
    category: 'Application',
    definition: 'A ZK proof that a value belongs to a set without revealing which specific value it is.',
    example: 'Proving you\'re on an approved list without revealing your identity. Proving you own a valid NFT without showing which one.',
    relatedTerms: ['zero-knowledge', 'privacy']
  }
};

const CATEGORIES = ['All', 'Core Concept', 'Technical', 'Technology', 'Security', 'Application', 'Proof System'];

const Glossary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTerm, setSelectedTerm] = useState(null);

  const filteredTerms = Object.entries(GLOSSARY_TERMS).filter(([key, term]) => {
    const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="glossary-page">
      <div className="glossary-header">
        <h1>Zero-Knowledge Glossary</h1>
        <p>Your guide to understanding zero-knowledge proofs and privacy-preserving computation</p>
      </div>

      <div className="glossary-controls">
        <input
          type="text"
          className="glossary-search"
          placeholder="Search terms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <div className="category-filters">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`category-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="glossary-content">
        <div className="terms-list">
          {filteredTerms.length === 0 ? (
            <div className="no-results">
              <p>No terms found matching "{searchTerm}"</p>
            </div>
          ) : (
            filteredTerms.map(([key, term]) => (
              <div
                key={key}
                className={`term-card ${selectedTerm === key ? 'selected' : ''}`}
                onClick={() => setSelectedTerm(selectedTerm === key ? null : key)}
              >
                <div className="term-header">
                  <h3>{term.term}</h3>
                  <span className="term-category">{term.category}</span>
                </div>
                
                <p className="term-definition">{term.definition}</p>
                
                {selectedTerm === key && (
                  <div className="term-details">
                    <div className="term-example">
                      <strong>Example:</strong>
                      <p>{term.example}</p>
                    </div>
                    
                    {term.relatedTerms && term.relatedTerms.length > 0 && (
                      <div className="related-terms">
                        <strong>Related Terms:</strong>
                        <div className="related-tags">
                          {term.relatedTerms.map(relatedKey => (
                            <button
                              key={relatedKey}
                              className="related-tag"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTerm(relatedKey);
                                // Scroll to term
                                setTimeout(() => {
                                  const element = document.querySelector(`[data-term="${relatedKey}"]`);
                                  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }, 100);
                              }}
                            >
                              {GLOSSARY_TERMS[relatedKey]?.term || relatedKey}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="glossary-footer">
        <p>
          <strong>Need more help?</strong> Visit the tutorials to learn these concepts hands-on, 
          or try the interactive games to see zero-knowledge in action!
        </p>
      </div>
    </div>
  );
};

export default Glossary;

